import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api.js";
import { compactTokens, usageLimit, usagePct } from "../lib/usage.js";

/**
 * Always-visible team-usage indicator for the header (b2c only). Measures
 * against the monthly token allowance, mirroring the billing page so the chip
 * and the page agree on what boundary the user is approaching. Turns red only
 * when the allowance is spent AND there are no prepaid credits left (the hard
 * stop). Renders nothing until there's an active team with a usage figure, so
 * it never clutters the header for a brand-new user with no team yet. The
 * caller gates on clerk + stripeEnabled, so this never mounts in enterprise.
 */
export function UsageChip() {
  const usage = useQuery({
    queryKey: ["billing", "usage"],
    queryFn: () => api.billingUsage(),
    retry: false,
    // Usage drifts slowly and is bumped server-side per turn; a short stale
    // window keeps the chip fresh without hammering the endpoint.
    staleTime: 30_000,
  });

  const data = usage.data;
  if (!data?.activeTeam || data.periodTokens === undefined) return null;

  const used = data.periodTokens;
  const credits = data.creditBalance ?? 0;
  const limit = usageLimit(data);
  const pct = usagePct(used, limit);
  const overAllowance = limit > 0 && used >= limit;
  const blocked = overAllowance && credits <= 0;
  const near = limit > 0 && pct >= 80 && !overAllowance;

  const barColor = blocked
    ? "bg-red-500"
    : overAllowance || near
      ? "bg-[var(--color-mx-gold)]"
      : "bg-[var(--color-mx-electric)]";

  const title =
    limit > 0
      ? `${used.toLocaleString()} / ${limit.toLocaleString()} tokens this period (monthly allowance)${
          credits > 0 ? ` · ${credits.toLocaleString()} credit tokens` : ""
        }`
      : `${used.toLocaleString()} tokens used this period`;

  return (
    <Link
      to="/billing"
      title={title}
      aria-label={title}
      className="hidden sm:flex items-center gap-2 text-white/55 hover:text-white transition-colors duration-150"
    >
      {limit > 0 ? (
        <>
          <span className="w-10 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <span
              className={`block h-full rounded-full transition-all ${barColor}`}
              style={{ width: `${pct}%` }}
            />
          </span>
          <span className="mx-mono-meta tabular-nums">{pct}%</span>
        </>
      ) : (
        <span className="mx-mono-meta tabular-nums">{compactTokens(used)} tokens</span>
      )}
    </Link>
  );
}
